// 관리자 비밀번호 변경 화면.
// 진입 경로
//  - 첫 시드 로그인 직후(must_change_password=1) AdminLoginPage 에서 강제 이동
//  - AdminProtectedRoute 가드가 must_change_password=1 인 세션을 다른 라우트에서 본 화면으로 돌려보냄
// 성공 시 서버는 모든 활성 리프레시 토큰을 일괄 폐기하므로, 클라이언트는 store 를 비우고 다시 로그인 화면으로 보낸다.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input } from "@oo/ui";
import { changeAdminPassword } from "@/features/auth/authApi.ts";
import { ApiError } from "@/api/client.ts";
import { useAdminAuthStore } from "@/stores/adminAuthStore.ts";

// 관리자 비밀번호 변경 화면 컴포넌트.
// 폼 검증은 백엔드 zod(영문+숫자, 10자 이상) 와 동일 규칙으로 미러링한다.
export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const clearSession = useAdminAuthStore((s) => s.clearSession);

  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [newPasswordConfirm, setNewConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const passwordPolicyOk =
    newPassword.length >= 10 && /[A-Za-z]/u.test(newPassword) && /\d/u.test(newPassword);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!currentPassword) {
      setError("현재 비밀번호를 입력해 주세요.");
      return;
    }
    if (!passwordPolicyOk) {
      setError("새 비밀번호는 영문과 숫자를 포함한 10자 이상이어야 합니다.");
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError("새 비밀번호와 확인 값이 다릅니다.");
      return;
    }
    if (currentPassword === newPassword) {
      setError("새 비밀번호는 기존과 달라야 합니다.");
      return;
    }
    setSubmitting(true);
    try {
      await changeAdminPassword(currentPassword, newPassword);
      // 서버가 모든 리프레시 토큰을 일괄 폐기했으므로 현재 세션도 비우고 다시 로그인 시킨다.
      clearSession();
      navigate("/login", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-6 py-10">
      <header className="flex flex-col gap-2 text-center">
        <h1 className="text-2xl font-bold text-brand-700">비밀번호 변경</h1>
        <p className="text-base text-ink-600">
          첫 로그인 직후이거나 비밀번호 재설정이 필요한 상태입니다. 새 비밀번호를 설정해 주세요.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4 rounded-lg bg-surface p-6 shadow-card"
      >
        <Input
          label="현재 비밀번호"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          label="새 비밀번호"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNew(e.target.value)}
          hint="영문과 숫자를 포함한 10자 이상."
        />
        <Input
          label="새 비밀번호 확인"
          type="password"
          autoComplete="new-password"
          value={newPasswordConfirm}
          onChange={(e) => setNewConfirm(e.target.value)}
          error={error ?? undefined}
        />
        <Button
          type="submit"
          fullWidth
          loading={submitting}
          disabled={!currentPassword || !passwordPolicyOk || newPassword !== newPasswordConfirm}
        >
          {submitting ? "변경 중..." : "비밀번호 변경"}
        </Button>
      </form>
    </main>
  );
}
